/**
 * Asteroid visual kit: per-archetype geometries by LOD plus materials (docs/art/sheets/asteroid-set.md).
 * Asset-lane owned (task A10). Gameplay owns the field layout, instancing, LOD bucketing and colliders.
 * P0 STUB: seeded plane-wave-displaced icospheres, flat per-archetype colours, no triplanar set.
 *
 * Instancing contract (see ASTEROID_ATTR in ../contracts): one InstancedMesh per (archetype, LOD) using a
 * CLONE of `geometries[a][l]` (the clone carries the aSeed / aSpin InstancedBufferAttributes) and `materials[a]`.
 * Geometries have a mean radius of 1 m: the instance scale is the rock's mean radius.
 * Note (three r186): NodeMaterial.positionNode is applied AFTER instancing, so vertex-stage spin must rotate
 * positionGeometry and apply the instance matrix itself.
 */
import { IcosahedronGeometry, type BufferGeometry, type Material } from 'three/webgpu'
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js'
import { createRng, range } from '../../core/rng'
import type { QualityPreset } from '../contracts'
import type { MaterialLibrary } from '../materials'
import { internals } from '../materials/library'
import { webgl2CapsOf } from '../webgl2'
import { asteroidLodDistancesFor, loadEnvironmentData } from './data'
import { createRockMaterial, type RockLook } from './rockMaterial'

export type AsteroidArchetypeId = 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
export const ASTEROID_ARCHETYPES: readonly AsteroidArchetypeId[] = ['A', 'B', 'C', 'D', 'E', 'F']

export interface AsteroidKit {
  /** Archetype order used by `geometries` and `materials`. */
  readonly archetypes: readonly AsteroidArchetypeId[]
  /** [archetype][lod], mean radius 1 m. LOD0 densest. */
  readonly geometries: readonly (readonly BufferGeometry[])[]
  /**
   * LOD switch-in distance per metre of rock radius: level l shows from `lodDistances[l] × radius`.
   * From environment.json `asteroids` (data × the preset's `lodScale`; PERF PF-3).
   */
  readonly lodDistances: readonly number[]
  readonly materials: readonly Material[]
  /** Ball collider radius = instance radius × colliderScale (spec §8: 0.8). */
  readonly colliderScale: number
}

const DETAIL = [15, 7, 3] // IcosahedronGeometry detail: 5,120 / 1,280 / 320 triangles (contract §4.3)
// Colours follow the asteroid concept (docs/art/concepts/asteroid-set): readable brown-greys, lifted from the first
// P0 values (C-type L* 18-23 read as black silhouettes under the key; D-047 lighting overhaul).
const SHAPE: Record<AsteroidArchetypeId, { radii: [number, number, number]; amp: number; seed: number; look: RockLook }> = {
  A: { radii: [1.0, 0.74, 0.84], amp: 0.2, seed: 11, look: { color: '#4B453F', roughness: 0.93, veinMetal: 0, craterScale: 1.6, craterDepth: 0.07, relief: 0.05 } },
  B: { radii: [1.0, 0.56, 0.66], amp: 0.06, seed: 23, look: { color: '#7A7268', roughness: 0.88, veinMetal: 0, craterScale: 1.8, craterDepth: 0.05, relief: 0.04 } },
  C: { radii: [0.96, 0.86, 0.91], amp: 0.09, seed: 37, look: { color: '#5F5851', roughness: 0.9, veinMetal: 0, craterScale: 2, craterDepth: 0.06, relief: 0.04 } },
  D: { radii: [1.0, 0.51, 0.56], amp: 0.11, seed: 41, look: { color: '#534C45', roughness: 0.93, veinMetal: 0, craterScale: 1.7, craterDepth: 0.06, relief: 0.05 } },
  E: { radii: [0.96, 0.62, 0.73], amp: 0.09, seed: 37, look: { color: '#6E6962', roughness: 0.72, veinMetal: 0.85, craterScale: 1.5, craterDepth: 0.04, relief: 0.03 } },
  F: { radii: [0.86, 0.8, 0.86], amp: 0.2, seed: 11, look: { color: '#4A443E', roughness: 0.9, veinMetal: 0, craterScale: 1.4, craterDepth: 0.08, relief: 0.06 } },
}

function rockGeometry(id: AsteroidArchetypeId, detail: number): BufferGeometry {
  const s = SHAPE[id]
  const rng = createRng(s.seed)
  const waves: number[] = []
  for (let k = 0; k < 10; k++) {
    const f = 2 * Math.pow(8, k / 9)
    const u = range(rng, -1, 1)
    const phi = range(rng, 0, Math.PI * 2)
    const ring = Math.sqrt(1 - u * u)
    waves.push(ring * Math.cos(phi), u, ring * Math.sin(phi), f, range(rng, 0, Math.PI * 2), 1 / f)
  }
  const g = mergeVertices(new IcosahedronGeometry(1, detail).deleteAttribute('normal').deleteAttribute('uv'))
  const pos = g.getAttribute('position')
  let sum = 0
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    let n = 0
    for (let w = 0; w < waves.length; w += 6) n += waves[w + 5] * Math.sin(waves[w + 3] * (x * waves[w] + y * waves[w + 1] + z * waves[w + 2]) + waves[w + 4])
    const r = 1 + s.amp * n
    pos.setXYZ(i, x * r * s.radii[0], y * r * s.radii[1], z * r * s.radii[2])
    sum += Math.hypot(pos.getX(i), pos.getY(i), pos.getZ(i))
  }
  const mean = sum / pos.count
  g.scale(1 / mean, 1 / mean, 1 / mean)
  g.computeVertexNormals()
  g.computeBoundingSphere()
  return g
}

export function createAsteroidKit(lib: MaterialLibrary, seed: number, preset: QualityPreset): AsteroidKit {
  void seed
  const lodDistances = asteroidLodDistancesFor(loadEnvironmentData(), preset)
  if (lodDistances.length !== DETAIL.length) throw new Error(`createAsteroidKit: environment.json asteroids.lodDistances needs ${DETAIL.length} levels`)
  const geometries = ASTEROID_ARCHETYPES.map((id) => DETAIL.map((d) => rockGeometry(id, d)))
  // The WebGL2 fallback drops the procedural rock detail (webgl2.json `rockDetail`, D-053).
  const detail = webgl2CapsOf(internals(lib).renderer)?.rockDetail ?? true
  const materials = ASTEROID_ARCHETYPES.map((id) => createRockMaterial(id, SHAPE[id].look, detail))
  return { archetypes: ASTEROID_ARCHETYPES, geometries, lodDistances, materials, colliderScale: 0.8 }
}
